import Vue from 'vue'
import { isEmpty as isObjectEmpty } from '@/helpers/object'
import { removeFromArray } from '@/helpers/array'
import { capitalizeFirstLetter } from '@/helpers/string'

export const OVERLAY_IDS = {
  confirm: 'confirm',
  countryChange: 'country-change',
  cyclonNavigation: 'cyclon-navigation',
  exploreFilter: 'explore-filter',
  exploreSort: 'explore-sort',
  dynamic: 'dynamic',
  leaveReview: 'leave-review',
  outOfStock: 'out-of-stock',
  quickView: 'quick-view',
  restrictedZipCode: 'restricted-zip-code',
  shoeMatcher: 'shoe-matcher',
  sizeGuide: 'size-guide',
}

export const OVERLAY_COMPONENT_NAMES = {
  [OVERLAY_IDS.confirm]: 'OverlayConfirm',
  [OVERLAY_IDS.countryChange]: 'OverlayCountryChange',
  [OVERLAY_IDS.cyclonNavigation]: 'OverlayCyclonNavigation',
  [OVERLAY_IDS.dynamic]: 'OverlayDynamic',
  [OVERLAY_IDS.exploreFilter]: 'OverlayExploreFilter',
  [OVERLAY_IDS.exploreSort]: 'OverlayExploreSort',
  [OVERLAY_IDS.leaveReview]: 'OverlayLeaveReview',
  [OVERLAY_IDS.outOfStock]: 'OverlayOutOfStock',
  [OVERLAY_IDS.quickView]: 'OverlayQuickView',
  [OVERLAY_IDS.restrictedZipCode]: 'OverlayRestrictedZipCode',
  [OVERLAY_IDS.shoeMatcher]: 'OverlayShoeMatcher',
  [OVERLAY_IDS.sizeGuide]: 'OverlaySizeGuide',
}

export const URL_PARAMS_TO_SHOW_OVERLAYS = {
  [OVERLAY_IDS.shoeMatcher]: 'shoe_matcher',
}

export const isValidId = value => Object.values(OVERLAY_IDS).includes(value)

// A show, hide, and preload action are auto-generated for each OVERLAY_IDS
// i.e. OVERLAY_IDS.sizeGuide will have showSizeGuideOverlay(props) and preloadSizeGuideOverlay()
const AUTO_GENERATED_ACTIONS = {
  show: 'show',
  hide: 'hide',
  preload: 'preload',
}

function getActionFunction(action, id) {
  const functions = {
    [AUTO_GENERATED_ACTIONS.show]: function ({ dispatch }, props = {}) {
      dispatch('show', { id, props })
    },
    [AUTO_GENERATED_ACTIONS.hide]: function ({ dispatch }) {
      dispatch('hide', { id })
    },
    [AUTO_GENERATED_ACTIONS.preload]: function ({ dispatch }) {
      dispatch('preload', { id })
    },
  }
  return functions[action]
}

function generateActionForEachOverlayIds(store) {
  Object.values(AUTO_GENERATED_ACTIONS).forEach(action => {
    for (const key in OVERLAY_IDS) {
      const actionName = `${action}${capitalizeFirstLetter(key)}Overlay`
      store.actions[actionName] = getActionFunction(action, OVERLAY_IDS[key])
    }
  })
}

function getIdFromPayload(payload) {
  return payload.id || payload
}

const storeSettings = {
  namespaced: true,
  state: {
    components: {},
    props: {},

    /**
     * @property {String[]} visibleOverlayIds
     * @property {String[]} renderedOverlayIds
     * @property {String[]} leavingOverlayIds
     *
     * List of overlay ids for a state. First of all, an overlay is added to renderedOverlayIds
     * from the show action which renders the overlay component in OverlayLoader.vue. The ids are
     * only added to visibleOverlayIds from Overlay.vue to be able to control the visibility from
     * the specific overlay component (i.e. OverlayDynamic has an init delay).
     *
     * We need two different states since we have to render the component to know if it will wait
     * before showing the overlay, but only prevent the page from scrolling if the overlay is
     * visible.
     *
     * Finally, the hide action will add the id to leavingOverlayIds which will fade out the
     * component and remove the id from all 3 state arrays on after-leave. We use a different state
     * to be able to rely on the fact that if an id is included in visibleOverlayIds, it means
     * it is still visible even when it's fading out.
     */
    visibleOverlayIds: [],
    renderedOverlayIds: [],
    leavingOverlayIds: [],
  },

  mutations: {
    addComponent(state, { id }) {
      if (state.components[id] || !isValidId(id) || !OVERLAY_COMPONENT_NAMES[id]) return

      const component = () => {
        const componentName = OVERLAY_COMPONENT_NAMES[id]

        return import(
          /* webpackChunkName: "[request]" */ `@/components/_shared/Overlay/${componentName}/${componentName}.vue`
        )
      }
      Vue.set(state.components, id, component)
    },

    setProps(state, payload) {
      if (isValidId(payload.id) && !isObjectEmpty(payload.props)) {
        Vue.set(state.props, payload.id, payload.props)
      }
    },

    setOverlayVisibility(state, payload) {
      if (!isValidId(payload.id)) return

      removeFromArray(state.visibleOverlayIds, payload.id)
      if (payload.isVisible) state.visibleOverlayIds.push(payload.id)
    },

    setOverlayRenderedState(state, payload) {
      if (!isValidId(payload.id)) return

      // If the targeted overlay is already rendered, remove it from the renderedOverlayIds
      // and push it back into the array to have it at the end which means it is the one
      // visible on top of all the visible overlays.
      removeFromArray(state.renderedOverlayIds, payload.id)
      if (payload.isRendered) state.renderedOverlayIds.push(payload.id)
    },

    setLeavingOverlayIds(state, payload) {
      if (payload.isLeaving && !state.leavingOverlayIds.includes(payload.id)) {
        state.leavingOverlayIds.push(payload.id)
      } else if (!payload.isLeaving) {
        removeFromArray(state.leavingOverlayIds, payload.id)
      }
    },
  },

  actions: {
    /**
     * Show an overlay based on its id.
     *
     * @name show
     * @function
     * @param {object} context - store context
     * @param {string|object} payload - the id itself or an object containing the id and props
     * @param {string} payload.id - a valid overlay id
     * @param {object} payload.props - an object setting the value of the props (optional)
     */
    show({ commit }, payload) {
      const id = getIdFromPayload(payload)
      if (payload.props) commit('setProps', payload)

      commit('addComponent', { id })
      commit('setOverlayRenderedState', { id, isRendered: true })
    },

    hide({ commit }, payload) {
      const id = getIdFromPayload(payload)
      commit('setLeavingOverlayIds', { id, isLeaving: true })
    },

    preload({ state, commit }, payload) {
      const id = getIdFromPayload(payload)

      commit('addComponent', { id })

      // make the browser download the scripts (doesn't render the component)
      return state.components[id]()
    },
  },

  getters: {
    renderedComponents: state => {
      return state.renderedOverlayIds.map(id => {
        return { is: state.components[id], id, props: state.props[id] }
      })
    },
    hasVisibleOverlays: state => !!state.visibleOverlayIds.length,
  },
}
generateActionForEachOverlayIds(storeSettings)

export default storeSettings
